import { CreateTaskDialog } from '@/components/CreateTaskDialog';
import Layout from '@/components/Layout';
import { ProjectBoard } from '@/components/ProjectBoard';
import { ProjectList } from '@/components/ProjectList';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/context/AuthContext';
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from '@/components/AiCommandPalette';
import { useEffect, useState, Fragment, useRef } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { SEO } from "@/components/SEO";

import { ProjectHeader } from "@/components/ProjectPage/ProjectHeader";

const ProjectPage = () => {
    const { organizationSlug, projectKey, view } = useParams();
    const [storedSettings, setStoredSettings] = useState(null);
    const [previousView, setPreviousView] = useState(null);
    const stateRef = useRef({ view, previousView });
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const [project, setProject] = useState(null);
    const { user, axiosInstance } = useAuth();
    const { setCurrentAiPageContext } = useAiPageContext();

    const [loadingStates, setLoadingStates] = useState({
        core: true,
        board: true,
        list: true,
    });


    // Get filter parameters from URL with "manual" as default sort
    const filterBy = storedSettings?.filterBy || 'none';
    const sortBy = storedSettings?.sortBy || 'manual';
    const groupByAssignee = storedSettings?.groupBy === 'assignee';

    // Get selected members from URL
    const selectedMembers = new Set(
        storedSettings?.filterByAssignee ? storedSettings?.filterByAssignee.split(',') : []
    );

    // Add new state parameter from URL
    const showBacklogColumn = storedSettings?.showBacklog;

    const updateBoardLocalStorage = (updates) => {
        const currentBoard = JSON.parse(localStorage.getItem('board')) || {};
        const newBoard = { ...currentBoard, ...updates };
        setStoredSettings(newBoard);
        localStorage.setItem('board', JSON.stringify(newBoard));
    };

    const handleLocalStorageLoad = () => {
        const storedBoard = JSON.parse(localStorage.getItem('board'));
        if (storedBoard && storedBoard.projectKey === projectKey) {
            setStoredSettings(storedBoard);
        } else {
            const newBoard = {
                projectKey,
                filterBy: 'none',
                sortBy: 'manual',
                groupBy: false,
                showBacklog: view === 'list' ? true : false,
                view: view
            }
            setStoredSettings(newBoard);
            localStorage.setItem('board', JSON.stringify(newBoard));
        }
    }

    // Handle member filter toggle with new parameter name
    const toggleMemberFilter = (memberId) => {
        const currentMembers = new Set(selectedMembers);
        if (currentMembers.has(memberId)) {
            currentMembers.delete(memberId);
        } else {
            currentMembers.add(memberId);
        }

        // Update URL with new member selection using new parameter name
        if (currentMembers.size > 0) {
            updateBoardLocalStorage({filterByAssignee: Array.from(currentMembers).join(',')});
        } else {
            updateBoardLocalStorage({filterByAssignee: null});
        }
    };

    // Handle filter changes
    const handleFilterChange = (value) => {
        if (value === 'assigned') {
            updateBoardLocalStorage({
                filterBy: 'none',
                filterByAssignee: user.id
            });
        } else {
            updateBoardLocalStorage({
                filterBy: value,
                filterByAssignee: null
            });
        }
    };

    // Handle sort changes
    const handleSortChange = (value) => {
        updateBoardLocalStorage({ sortBy: value });
    };

    // Handle group by changes
    const handleGroupByChange = (checked) => {
        updateBoardLocalStorage({ groupBy: checked ? 'assignee' : null });
    };

    // Add new handler
    const handleShowBacklogChange = (checked) => {
        updateBoardLocalStorage({ showBacklog: checked ? true : null });
    };

    const isCreateDialogOpen = location.pathname.endsWith('/new');

    const handleOpenChange = (open) => {
        if (open) {
            navigate(`/${organizationSlug}/plan/${projectKey}/${view}/new`);
        } else {
            navigate(`/${organizationSlug}/plan/${projectKey}/${view}`);
        }
    };

    const fetchProject = async () => {
        try {
            const response = await axiosInstance.get(`/projects/${organizationSlug}/${projectKey}`);
            const projectData = response.data;
            setProject(projectData);

            // Set page context for AI Command Palette with custom loading texts
            const customLoadingTexts = [
                {
                    text: `Loading ${projectData.name} data`,
                    showOnce: true,
                    minTime: 2000,
                    maxTime: 3000
                },
                {
                    text: `Analyzing ${projectData.name} tasks`,
                    showOnce: false,
                    minTime: 2000,
                    maxTime: 4000
                },
                {
                    text: "Thinking",
                    showOnce: false,
                    minTime: 1500,
                    maxTime: 2500
                },
                {
                    text: "Processing project information",
                    showOnce: false,
                    minTime: 2500,
                    maxTime: 3500
                }
            ];

            setCurrentAiPageContext(
                contextHelpers.createProjectContext(projectData, customLoadingTexts)
            );
        } catch (error) {
            console.error('Error fetching project:', error);
            toast({
                description: "Failed to fetch project details",
                variant: "destructive"
            });
        } finally {
            setLoadingStates({
                core: false,
                board: false,
                list: false,
            })
        }
    };

    useEffect(() => {
        stateRef.current = { view, previousView };
    }, [view, previousView]);

    useEffect(() => {
        if (view && view !== previousView) {
            setPreviousView(view);
        }
    }, [view]);

    useEffect(() => {
        fetchProject();
        handleLocalStorageLoad();

        const handlePopState = () => {
            const currentState = stateRef.current;

            if (currentState.view !== currentState.previousView) {
                if (currentState.previousView === 'list' && currentState.view === 'board') {
                    updateBoardLocalStorage({ showBacklog: false });
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [projectKey, organizationSlug]);

    const handleViewChange = (newView) => {
        updateBoardLocalStorage({ showBacklog: newView === 'list' ? true : false, view: newView });
        if (newView !== view) {
            navigate(`/${organizationSlug}/plan/${projectKey}/${newView}`);
        }
    };

    const handleTaskCreated = async () => {
        await fetchProject();
        handleOpenChange(false);
    };



    return (
        <Layout>
            <SEO title={project?.name ? `${project.name} › Active week` : 'Active week'} />

            <div className="space-y-4 flex flex-col h-full">
                <ProjectHeader
                    isLoading={loadingStates.core}
                    project={project}
                    selectedMembers={selectedMembers}
                    toggleMemberFilter={toggleMemberFilter}
                    user={user}
                    filterBy={filterBy}
                    sortBy={sortBy}
                    groupByAssignee={groupByAssignee}
                    showBacklogColumn={showBacklogColumn}
                    view={view}
                    handleFilterChange={handleFilterChange}
                    handleSortChange={handleSortChange}
                    handleGroupByChange={handleGroupByChange}
                    handleShowBacklogChange={handleShowBacklogChange}
                    handleViewChange={handleViewChange}
                    handleOpenChange={handleOpenChange}
                />

                <div className="flex-grow">
                    {view === 'board' ? (
                        <ProjectBoard
                            isLoading={loadingStates.board}
                            project={project}
                            setProject={setProject}
                            groupByAssignee={groupByAssignee}
                            showBacklogColumn={showBacklogColumn}
                            filterByAssignee={selectedMembers}
                            filterBy={filterBy}
                            sortBy={sortBy}
                            user={user}
                        />
                    ) : (
                        <ProjectList
                            isLoading={loadingStates.list}
                            project={project}
                            setProject={setProject}
                            groupByAssignee={groupByAssignee}
                            showBacklogColumn={showBacklogColumn}
                            filterByAssignee={selectedMembers}
                            filterBy={filterBy}
                            sortBy={sortBy}
                            user={user}
                        />
                    )}
                </div>
            </div>
            <CreateTaskDialog
                open={isCreateDialogOpen}
                onOpenChange={handleOpenChange}
                project={project}
                onTaskCreated={handleTaskCreated}
            />
        </Layout>
    );
};

const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Loading project details...</h1>
    </div>
);

export default ProjectPage;